import React from 'react'
import {
	Container,
	Grid,
	Box,
	TextField,
	Button,
	Typography,
	InputAdornment,
	Stack,
	IconButton,
	Checkbox,
	FormControlLabel,
	FormControl,
	RadioGroup,
	Radio,
	useTheme,
} from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import Carousel from 'react-multi-carousel'
import 'react-multi-carousel/lib/styles.css'
/** ICONS */
import PhoneNumberIcon from '@mui/icons-material/PhoneIphone'
import EmailIcon from '@mui/icons-material/Email'
import LockIcon from '@mui/icons-material/Lock'
import PersonIcon from '@mui/icons-material/Person'
import VisibleIcon from '@mui/icons-material/Visibility'
import InvisibleIcon from '@mui/icons-material/VisibilityOff'
/** COMPONENTS */
import CarouselItem from '@/components/carousel/item'
import CustomDots from '@/components/carousel/dots'

const Login = () => {
	const [phoneNumber, setPhoneNumber] = React.useState('')
	const [password, setPassword] = React.useState('')
	const [showPassword, setShowPassword] = React.useState(false)
	const [rememberMe, setRememberMe] = React.useState(false)

	const [isLoading, setIsLoading] = React.useState(false)
	const [type, setType] = React.useState('post-paid')

	const [errorFields, setErrorFields] = React.useState<string[]>([])

	const snackbar = useSnackbar()

	const handleShowPassword = () => {
		setShowPassword(!showPassword)
	}

	const navigate = useNavigate()

	const login = (e?: React.FormEvent) => {
		if (e) e.preventDefault()
		setIsLoading(true)
		fetch(`${process.env.REACT_APP_API_URL}/auth/login`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				username: phoneNumber,
				password,
				type,
				rememberMe,
			}),
		})
			.then((res) => res.json())
			.then((data) => {
				if (data.success && data.token) {
					snackbar.enqueueSnackbar('Амжилттай нэвтэрлээ', {
						variant: 'success',
						autoHideDuration: 6000,
					})

					// store token to local storage
					localStorage.setItem('token', data.token)
					// redirect to home page
					navigate('/')
				} else {
					snackbar.enqueueSnackbar(data.message, {
						variant: 'error',
						autoHideDuration: 6000,
					})

					// add data.field to errorFields
					setErrorFields([data.field])
				}
			})
			.finally(() => {
				setIsLoading(false)
			})
	}

	React.useEffect(() => {
		document.title = 'Нэвтрэх - ЭН БИ СИ'
	}, [])

	const theme = useTheme();

	React.useEffect(() => {
		if(theme.palette.mode === 'light') {
			document.body.style.backgroundColor = theme.palette.background.default
		} else {
			document.body.style.backgroundColor = theme.palette.background.default
		}
	}, [theme.palette.mode])

	return (
		<Container sx={(theme) => ({
			backgroundColor: theme.palette.mode === 'light' ? 'background.default' : 'background.default',
		})}>
			<Grid
				container
				sx={{
					height: '100vh',
				}}
			>
				<Grid
					item
					xs={12}
					md={5}
					sx={{
						padding: 2,
					}}
				>
					<Box
						sx={(theme) => ({
							height: '100%',
							backgroundColor: theme.palette.mode === 'light' ? 'primary.main' : 'dark',
							borderRadius: 1,
							color: '#fff',
							display: 'flex',
							flexDirection: 'column',
							overflow: 'hidden',
						})}
					>
						<Carousel
							swipeable={false}
							draggable={false}
							showDots={true}
							responsive={{
								desktop: {
									breakpoint: {
										max: 3000,
										min: 1024,
									},
									items: 1,
								},
							}}
							arrows={false}
							infinite
							containerClass="carousel-container"
							customDot={<CustomDots items={2} />}
						>
							<CarouselItem
								title="Хамт олондоо"
								description="Таны ажлын бүтээмжийг нэмэгдүүлж цаг хэмнэх үүднээс бид системээ байнга сайжруулж байна."
								buttonText="Бүртгүүлэх"
								author={{
									avatar: 'https://avatars.githubusercontent.com/u/39462474?v=4',
									name: 'Бардорж',
									caption: 'IT Хэлтэс',
								}}
							/>
							<CarouselItem
								title="Таны санал хамгийн үнэ цэнтэй"
								description="ЭН БИ СИ биллин & дотоод системийг илүү сайжруулхын тулд та бүгдийн санал хүсэлт хамгийн үнэ цэнтэй байх болно."
								buttonText="Бүртгүүлэх"
								author={{
									avatar: 'https://avatars.githubusercontent.com/u/39462474?v=4',
									name: 'Маналжав',
									caption: 'IT Engineer',
								}}
							/>
						</Carousel>
					</Box>
				</Grid>
				<Grid item xs={12} md={7}>
					<form noValidate autoComplete="off" onSubmit={login}>
						<Box
							sx={{
								height: '100%',
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'center',
							}}
						>
							<Box
								sx={{
									padding: 8,
								}}
							>
								<Typography variant="h4" gutterBottom>
									Нэвтрэх
								</Typography>
								<Typography variant="body1">
									Хэрэв танд бүртгэл үүсээгүй бол доорх холбоос дээр дарж бүртгүүлнэ үү
								</Typography>
								<Stack direction={'row'} spacing={2} sx={{ mt: 4 }}>
									<FormControlLabel
										value="post-paid"
										control={<Radio />}
										label="Дараа төлбөрт"
										onChange={() => setType('post-paid')}
										checked={type === 'post-paid'}
										sx={{
											borderRadius: 2,
											border: '2px solid #ccc',
											borderColor: type === 'post-paid' ? 'primary.main' : '#ccc',
											p: 1,
											pl: 2,
											width: '100%',
										}}
									/>
									<FormControlLabel
										value="pre-paid"
										control={<Radio />}
										label="Урьдчилсан төлбөрт"
										onChange={() => setType('pre-paid')}
										checked={type === 'pre-paid'}
										sx={{
											borderRadius: 2,
											border: '2px solid #ccc',
											borderColor: type === 'pre-paid' ? 'primary.main' : '#ccc',
											p: 1,
											pl: 2,
											width: '100%',
										}}
									/>
								</Stack>
								<Stack sx={{ mt: 4 }} spacing={3}>
									<TextField
										label="Нэвтрэх нэр"
										variant="outlined"
										placeholder="Username, утасны дугаар эсвэл имэйл"
										fullWidth
										margin="normal"
										error={errorFields.includes('phoneNumber')}
										onChange={(e) => setPhoneNumber(e.target.value)}
										inputProps={{
											maxLength: 52,
										}}
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<PhoneNumberIcon />
												</InputAdornment>
											),
										}}
										autoFocus
									/>
									<TextField
										label="Нууц үг"
										variant="outlined"
										fullWidth
										onChange={(e) => setPassword(e.target.value)}
										margin="normal"
										error={errorFields.includes('password')}
										type={showPassword ? 'text' : 'password'}
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<LockIcon />
												</InputAdornment>
											),
											endAdornment: (
												<InputAdornment position="end">
													<IconButton onClick={handleShowPassword} color="primary">
														{showPassword ? <VisibleIcon /> : <InvisibleIcon />}
													</IconButton>
												</InputAdornment>
											),
										}}
									/>
								</Stack>
								<FormControl fullWidth={false} sx={{ mt: 2 }}>
									<FormControlLabel
										control={
											<Checkbox
												checked={rememberMe}
												onChange={() => setRememberMe(!rememberMe)}
											/>
										}
										label="Сануулах"
									/>
								</FormControl>
								<Stack sx={{ mt: 2 }}>
									<LoadingButton
										variant="contained"
										size="large"
										onClick={login}
										type="submit"
										loading={isLoading}
										disableElevation
									>
										Нэвтрэх
									</LoadingButton>
								</Stack>
							</Box>
						</Box>
					</form>
				</Grid>
			</Grid>
		</Container>
	)
}

export default Login
